import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams, useHistory, useLocation} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {useQuery, QueryFunctionContext} from 'react-query';

import {ListResponse, SelectOption, User, Vehicle} from 'types';

import api from 'api';
import roles from 'constants/roles';
import states from 'constants/states';
import toast from 'react-hot-toast';
import {get} from 'lodash';
import {phoneFormat} from 'helpers/phoneFormat';
import useDeleteEmployee from './useDeleteEmployee';
import useAuth from 'hooks/useAuth';

interface Option {
  label: string;
  value: string | number;
}

interface FormValue {
  first_name: string;
  last_name: string;
  field_memebers: any[];
  travel_method: string;
  email: string;
  phone: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zipcode: string;
  state: Option;
  is_active: boolean;
  username: string;
  password: string;
  password_confirmation: string;
  role: Option;
  team?: Option;
  field_role: SelectOption & {id: number};
}

async function getVehicles() {
  const {data} = await api.get<ListResponse<Vehicle>>('/teams/');

  return data;
}

async function getEmployeeById({queryKey}: QueryFunctionContext) {
  const [, employeeId] = queryKey;

  if (!employeeId) return;

  const {data} = await api.get<User>(`/users/${employeeId}`);

  return data;
}

async function getPermissions({queryKey}: QueryFunctionContext<any[]>) {
  const [,] = queryKey;
  const {data} = await api.get<any[]>(`/permissions/menus/`, {
    params: {
      limit: 30,
    },
  });

  return data;
}

async function getUserPermissions({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId] = queryKey;
  if (!userId) return;
  const {data} = await api.get<any[]>(
    `/permissions/user-permissions/${userId}/`
  );

  return data;
}

async function getExistingRoles() {
  const {data} = await api.get<ListResponse<any>>(
    '/drivers/driver/field-roles/'
  );
  return data;
}

function useEmployeeForm(
  isModal: boolean,
  setUser?: (data: any) => void,
  sharedDefaultValue?: string
) {
  const {employeeId} = useParams<{employeeId: string}>();
  const {goBack, replace, push} = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [creatingFieldRole, setCreatingFieldRole] = useState(false);
  const {state} = useLocation();
  const {currentUser, rolePermission} = useAuth();
  const [userPermissions, setUserPermissions] = useState<Record<string, any>>(
    {}
  );

  const isStaff = get(state, 'isStaff', false);

  const teamId = get(state, 'state.team_id');
  const {
    handleSubmit,
    register,
    control,
    formState: {errors},
    reset,
    watch,
    setValue,
    setError,
  } = useForm<FormValue>({
    defaultValues: {
      is_active: true,
      first_name: sharedDefaultValue,
    },
  });

  const role = watch('role');

  const {data: vehicles, refetch} = useQuery('teams', getVehicles);
  const {data: employee, isLoading} = useQuery(
    ['employee', employeeId],
    getEmployeeById
  );
  const {data: permissions} = useQuery(['permissions_menu'], getPermissions, {
    enabled: currentUser?.role === 'management',
  });

  const {data: user_permissions} = useQuery(
    ['permissions_user', employeeId],
    getUserPermissions,
    {
      enabled: currentUser?.role === 'management',
      refetchOnWindowFocus: false,
    }
  );

  const {data: fieldRoles, refetch: refetchFieldRoles} = useQuery(
    ['field_roles'],
    getExistingRoles,
    {
      refetchOnWindowFocus: false,
    }
  );
  const fieldRoleOptions = useMemo(() => {
    return fieldRoles?.results?.map((role) => ({
      id: role.id,
      label: role?.name,
      value: role?.name,
    }));
  }, [fieldRoles]);

  const options = useMemo(
    function () {
      if (!vehicles) return [];

      return vehicles.results.map((v) => ({value: v.id, label: v.name}));
    },
    [vehicles]
  );
  const team = vehicles?.results.find((item) => item.id === teamId);

  useEffect(
    function () {
      if (employee) {
        const {driver, ...user} = employee;
        if (!user) {
          return;
        }
        reset({
          ...user,
          phone: phoneFormat(user.phone),
          state: states.find((s) => s.value === user.state),
          role: roles.find((r) => r.value === employee.role),
          team: employee?.driver?.team
            ? {
                value: employee.driver?.team.id,
                label: employee.driver?.team?.name,
              }
            : undefined,
          field_role: {
            label: get(employee, 'driver.field_role.name', ''),
            value: get(employee, 'driver.field_role.name', ''),
          },
          is_active: employee.is_active,
        });
      }
    },
    [employee, reset]
  );

  useEffect(() => {
    if (user_permissions) {
      const permissions: Record<string, any> = {};
      user_permissions.forEach((item) => {
        permissions[item.menu.id] = {
          ...item,
        };
      });
      setUserPermissions(permissions);
    }
  }, [user_permissions]);

  async function submit(data: FormValue) {
    if (data.phone && data.phone.length !== 14) {
      setError('phone', {
        message: 'Enter a valid phone number',
      });
      return;
    }

    setSubmitting(true);

    const user = {
      ...data,
      field_role: data?.field_role?.id,
      phone: data.phone.replace(/\D/g, ''),
      state: data.state?.value,
      role: data?.role?.value || 'driver',
      team: data.team?.value || teamId,
    };

    try {
      const newUser = await (employeeId
        ? api.patch<User>(`/users/${employeeId}/`, user)
        : api.post<User>('/users/', {...user, username: user.email}));
      if (setUser) setUser(newUser.data);

      if (currentUser?.role === 'management' && employeeId) {
        const menu_permissions = Object.values(userPermissions).map((menu) => {
          return {
            menu_slug: menu.menu.slug,
            permissions: menu.permissions,
          };
        });
        await api.post('/permissions/assign-permission/', {
          user_id: employeeId,
          menu_permissions,
        });
      }
      if (!isModal) {
        goBack();
      }

      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);

      const error_message =
        get(error, 'response.data.error') ||
        get(error, 'response.data.error_message') ||
        'Something went wrong';

      toast.error(error_message + '. Please upgrade your plan.');
      setTimeout(() => {
        push('/settings/plans');
      }, 1000);
    }
  }

  const setTeam = (data: Vehicle) => {
    refetch();
    setValue('team', {
      value: data.id,
      label: data.name,
    });
  };

  const {deleteEmployee, isDeleting} = useDeleteEmployee();

  const handleDeleteEmployee = useCallback(
    async (id: number) => {
      await deleteEmployee(id);
      replace(isStaff ? '/people/employees/' : '/people/teams');
    },
    [deleteEmployee, replace, isStaff]
  );

  const handleSendAppLink = async () => {
    const id = employee?.driver?.id;
    try {
      await api.post(`/drivers/${id}/send-sms/`);
      toast.success('App link sent successfully');
    } catch (error) {
      toast.error(get(error, 'message') || 'Failed to send app link');
    }
  };

  const onChangePermission = (
    menu: number,
    permission: 'write' | 'read' | 'delete'
  ) => {
    const copied = {...userPermissions};
    copied[menu].permissions[permission] =
      !copied[menu].permissions[permission];
    setUserPermissions(copied);
  };

  const onCreateFieldRoleOption = async (newRole: string) => {
    try {
      setCreatingFieldRole(true);
      const body = {
        name: newRole,
        is_general: true,
      };
      const res = await api.post('/drivers/driver/field-roles/', body);
      refetchFieldRoles();
      const {id, name} = res.data;
      setValue('field_role', {label: name, value: name, id: id});
    } catch (error) {
      toast.error('Failed to create new field role');
    } finally {
      setCreatingFieldRole(false);
    }
  };

  const canDelete =
    currentUser?.role === 'management'
      ? rolePermission['staff']?.permissions?.delete
      : rolePermission['staff']?.permissions?.delete;
  const canUpdate =
    currentUser?.role === 'management'
      ? rolePermission['staff']?.permissions?.write
      : rolePermission['staff']?.permissions?.write;

  return {
    isStaff,
    state,
    teamId,
    team,
    employee,
    employeeId,
    options,
    submitting,
    errors,
    control,
    role: role?.value,
    goBack,
    register,
    setTeam,
    watch,
    setValue,
    loading: isLoading && employeeId,
    handleSubmit: handleSubmit(submit),
    isDeleting,
    handleSendAppLink,
    handleDeleteEmployee,
    currentUser,
    userPermissions,
    fieldRoleOptions,
    creatingFieldRole,
    permissions: permissions || [],
    onChangePermission,
    canDelete,
    canUpdate,
    onCreateFieldRoleOption,
  };
}

export default useEmployeeForm;
